<template lang="pug">
  div
    Scanner(
      v-if="showScanInterface",
      :devices="devices",
      @hideScanner="showScanInterface = false; $emit('toggle:scanner', false);",
      @scan="handleFileChange($event)",
    )
    div.uploader.px-6.py-8(
      v-else-if="!showScanInterface && !file && isUpload",
      :class="{ drag : drag }",
      @dragenter.stop.prevent="drag = true",
      @dragleave.stop.prevent="drag = false",
      @dragover.stop.prevent,
      @drop.stop.prevent="handleDrop",
    )
      h3.mb-4 {{ $t("form.media.drop_message") }}
      div.d-flex
        v-btn.ma-2.white--text(
          color="primary",
          @click="$refs.uploader.click()",
        )
          v-icon.ml-1(left, dark) fas fa-cloud-upload-alt
          span.ml-2 {{ $t("form.media.drop_button") }}

        v-btn.ma-2.white--text(
          color="primary",
          v-if="enableScanner",
          @click="getVideoDevices()",
        )
          v-icon.ml-1(left, dark) fas fa-camera
          span.ml-2 {{ $t("form.media.scan_button") }}

      input(
        v-show="false",
        ref="uploader",
        type="file",
        tabindex="-1",
        accept=".JPG, .jpg, .jpeg, .mp4",
        @change="handleUpload",
        required,
      )
    .previewer.pa-2(v-else)
      div.d-flex(v-if="isUpload")
        div.text-preview.mr-2(
          v-if="isVideo",
        ) {{ info.name.split('.').pop() }}
        img.mr-2(
          v-else,
          :src="src"
        )
        div
          p.mb-0.title
            b {{ info.name }}
          p.mb-0 Size: {{ info.size / 1000000 }} Mb
      div.d-flex(v-else)
        div.text-preview.mr-2
          video.fill-height.full-width(v-if="isVideoSrc")
            source(:src='s3Location', type='video/mp4')
            source(:src='s3Location', type='video/ogg')
            | {{ $t("form.media.video_not_supported") }}
          v-img.fill-height(
            v-else,
            :src="s3Location",
            aspect-ratio="1",
          )
        div
          p.mb-0.title
            b.error--text(v-if="!editable") {{ $t("form.media.media_cannot_edit") }}
            b(v-else) {{ $t("form.media.media_override_warning") }}
      v-spacer
      div
        v-icon(
          v-if="isUpload || editable",
          @click="file = null; $emit('update:isUpload')",
        ) fas fa-times

    .error--text {{ errorMessages }}

</template>

<script>
import { mapGetters } from 'vuex';
import { filter, map, mergeRight, pick, propEq } from 'ramda';
import Scanner from '@/components/forms/inputs/Scanner.vue';

export default {
  props: {
    value: {
      validator: prop =>
        ['string', 'object', 'undefined'].includes(typeof prop),
      required: true,
    },

    errorMessages: {
      type: String,
      default: '',
    },

    isUpload: {
      type: Boolean,
      default: false,
    },

    // use in edit scenario
    isVideoSrc: {
      type: Boolean,
      default: false,
    },

    s3Location: {
      validator: prop => ['string', 'undefined'].includes(typeof prop),
      required: true,
    },

    editable: {
      type: Boolean,
      default: false,
    },

    enableScanner: {
      type: Boolean,
      default: false,
    },
  },

  components: {
    Scanner,
  },

  data: () => ({
    drag: false,
    info: {},
    src: null,
    isVideo: false,
    showScanInterface: false,
    devices: [],
  }),

  computed: {
    ...mapGetters({
      activeCamera: 'auth/activeCamera',
    }),

    file: {
      get() {
        return this.value;
      },
      set(file) {
        this.$emit('update:value', file);
      },
    },
  },

  beforeDestroy() {
    window.removeEventListener('keydown', this.showScanner);
  },

  mounted() {
    window.addEventListener('keydown', this.showScanner);
  },

  methods: {
    showScanner(event) {
      if (
        event.code === 'Space' &&
        !this.showScanInterface &&
        this.enableScanner
      ) {
        event.preventDefault();
        this.getVideoDevices();
        this.$emit('toggle:scanner', true);
      }
    },

    handleDrop($event) {
      this.drag = false;
      this.handleFileChange($event.dataTransfer.files[0]);
    },

    handleUpload(file) {
      this.handleFileChange(file.target.files[0]);
    },

    handleFileChange(file) {
      this.isVideo = file.type.includes('video');

      if (file.size > 32 * 1000 * 1000) {
        this.$emit('update:error', {
          media: 'File exceed size limit of 32 mb.',
        });
      } else {
        if (!this.isVideo) {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = e => (this.src = e.target.result);
        }

        this.info = pick(['name', 'size', 'type'], file);
        this.file = file;
      }
    },

    async getVideoDevices() {
      this.showScanInterface = true;
      await navigator.mediaDevices
        .getUserMedia({ video: true })
        .then(stream => {
          stream.getVideoTracks().forEach(track => track.stop());
        })
        .catch(() => {
          this.showScanInterface = false;
          window.$eventHub.$emit('showAlert', {
            message: 'No camera devices detected',
            type: 'error',
          });
        });

      this.devices = await navigator.mediaDevices
        .enumerateDevices()
        .then(filter(propEq('kind', 'videoinput')))
        .then(
          map((device, index) => ({
            deviceId: device.deviceId,
            groupId: device.groupId,
            kind: device.kind,
            label: device.label || `camera${index + 1}`,
          })),
        );

      if (this.devices.length > 0) {
        const activeCameraInList =
          this.devices.findIndex(propEq('deviceId', this.activeCamera)) > 0;

        if (!this.activeCamera || !activeCameraInList) {
          this.$store.dispatch(
            'auth/updateActiveCamera',
            this.devices[0].deviceId,
          );
        }
        this.$emit('toggle:scanner', true);
      }
    },
  },
};
</script>

<style lang="scss">
.uploader {
  border: 1px dashed #2f6947;
  background: rgba(#2f6947, 0.2);
  display: flex;
  flex-direction: column;
  align-items: center;

  &.drag {
    h3,
    button {
      pointer-events: none;
    }
    border-style: solid;
    background: rgba(#2f6947, 0.4);
  }
}

.previewer {
  border: 1px dotted black;
  display: flex;
  .text-preview {
    font-size: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 120px;
    height: 120px;
    background: rgba(#ee44aa, 0.2);
  }

  img {
    width: 120px;
    height: 120px;
  }
}
</style>
